import React, { useContext, useState, useEffect } from "react";
import { 
  Box, 
  Container, 
  Typography, 
  Card, 
  CardContent, 
  Avatar, 
  Button, 
  IconButton, 
  Paper, 
  Stack, 
  SvgIcon, 
  Tab, 
  Tabs,
  Divider,
  useTheme
} from "@mui/material";
import Grid from "@mui/material/Grid";
import {
  SaveAlt,
  Groups,
  FilterList,
  Clear,
  Call as CallIcon,
  HourglassEmpty as HourglassEmptyIcon,
  CheckCircle as CheckCircleIcon,
  RecordVoiceOver as RecordVoiceOverIcon,
  GroupAdd as GroupAddIcon,
} from "@mui/icons-material";
import * as XLSX from 'xlsx';
import { toast } from "react-toastify";
import { isArray, isEmpty } from "lodash";
import moment from "moment";
import TableAttendantsStatus from "../../components/Dashboard/TableAttendantsStatus";
import { AuthContext } from "../../context/Auth/AuthContext";
import useDashboard from "../../hooks/useDashboard";
import useContacts from "../../hooks/useContacts";
import useMessages from "../../hooks/useMessages";
import { ChatsUser } from "./ChartsUser";
import ChartDonut from "./ChartDonut";
import Filters from "./Filters";
import { ChartsDate } from "./ChartsDate";
import ForbiddenPage from "../../components/ForbiddenPage";
import { i18n } from "../../translate/i18n";

const Dashboard = () => {
  const theme = useTheme();
  const [counters, setCounters] = useState({});
  const [attendants, setAttendants] = useState([]);
  const [showFilter, setShowFilter] = useState(false);
  const [dateStartTicket, setDateStartTicket] = useState(moment().startOf('month').format("YYYY-MM-DD"));
  const [dateEndTicket, setDateEndTicket] = useState(moment().format("YYYY-MM-DD"));
  const [queueTicket, setQueueTicket] = useState(false);
  const [fetchDataFilter, setFetchDataFilter] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  
  const { find } = useDashboard();
  const { user } = useContext(AuthContext);

  let newDate = new Date();
  let date = newDate.getDate();
  let month = newDate.getMonth() + 1;
  let year = newDate.getFullYear();
  let nowIni = `${year}-${month < 10 ? `0${month}` : `${month}`}-01`;
  let now = `${year}-${month < 10 ? `0${month}` : `${month}`}-${date < 10 ? `0${date}` : `${date}`}`;

  useEffect(() => {
    async function firstLoad() {
      await fetchData();
    }
    setTimeout(() => {
      firstLoad();
    }, 1000);
  }, [fetchDataFilter]);

  async function fetchData() {
    setLoading(true);
    let params = {};
    if (!isEmpty(dateStartTicket) && moment(dateStartTicket).isValid()) {
      params = { ...params, date_from: moment(dateStartTicket).format("YYYY-MM-DD") };
    }
    if (!isEmpty(dateEndTicket) && moment(dateEndTicket).isValid()) {
      params = { ...params, date_to: moment(dateEndTicket).format("YYYY-MM-DD") };
    }
    if (Object.keys(params).length === 0) {
      toast.error("Parametrize o filtro");
      setLoading(false);
      return;
    }
    const data = await find(params);
    setCounters(data.counters);
    if (isArray(data.attendants)) {
      setAttendants(data.attendants);
    } else {
      setAttendants([]);
    }
    setLoading(false);
  }

  const exportarGridParaExcel = () => {
    const ws = XLSX.utils.table_to_sheet(document.getElementById('grid-attendants'));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'RelatorioDeAtendentes');
    XLSX.writeFile(wb, 'relatorio-de-atendentes.xlsx');
  };

  function formatTime(minutes) {
    return moment().startOf("day").add(minutes, "minutes").format("HH[h] mm[m]");
  }

  const GetUsers = () => {
    let userOnline = 0;
    attendants.forEach(user => {
      if (user.online === true) {
        userOnline = userOnline + 1;
      }
    });
    return userOnline;
  };

  const GetContacts = (all) => {
    let props = all ? {} : { dateStart: dateStartTicket, dateEnd: dateEndTicket };
    const { count } = useContacts(props);
    return count;
  };

  const GetMessages = (all, fromMe) => {
    let props = all
      ? { fromMe }
      : { fromMe, dateStart: dateStartTicket, dateEnd: dateEndTicket };
    const { count } = useMessages(props);
    return count;
  };

  function toggleShowFilter() {
    setShowFilter(!showFilter);
  }

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  if (user.profile === "user" && user.showDashboard === "disabled") {
    return <ForbiddenPage />;
  }

  const statCards = [
    {
      title: i18n.t("dashboard.cards.inAttendance"),
      value: counters.supportHappening || 0,
      icon: <CallIcon />,
      color: theme.palette.primary.main
    },
    {
      title: i18n.t("dashboard.cards.waiting"),
      value: counters.supportPending || 0,
      icon: <HourglassEmptyIcon />,
      color: theme.palette.secondary.main
    },
    {
      title: i18n.t("dashboard.cards.finalized"),
      value: counters.supportFinished || 0,
      icon: <CheckCircleIcon />,
      color: theme.palette.success.main
    },
    {
      title: i18n.t("dashboard.cards.groups"),
      value: counters.supportGroups || 0,
      icon: <Groups />,
      color: "#8e44ad" // Keep a custom color for this one
    },
    {
      title: i18n.t("dashboard.cards.activeAttendants"),
      value: `${GetUsers() || 0}/${attendants.length || 0}`,
      icon: <RecordVoiceOverIcon />,
      color: theme.palette.error.main
    },
    {
      title: i18n.t("dashboard.cards.newContacts"),
      value: counters.leads || 0,
      icon: <GroupAddIcon />,
      color: theme.palette.warning.main
    }
  ];

  return (
    <Box sx={{ backgroundColor: "#f5f7fa", minHeight: "100vh", py: 2 }}>
      <Container maxWidth="xl">
        {/* Header with filter button */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
          <Typography variant="h4" fontWeight="bold" color="primary">
            {i18n.t("dashboard.title") || "Dashboard"}
          </Typography>          
          
        </Box>

        {/* Filters Section */}
        {showFilter && (
          <Paper 
            sx={{ 
              p: 2, 
              mb: 3, 
              borderRadius: theme.shape.borderRadius,
              boxShadow: theme.shadows[1]
            }}
          >
            <Filters
              setDateStartTicket={setDateStartTicket}
              setDateEndTicket={setDateEndTicket}
              dateStartTicket={dateStartTicket}
              dateEndTicket={dateEndTicket}
              setQueueTicket={setQueueTicket}
              queueTicket={queueTicket}
              fetchData={setFetchDataFilter}
            />
          </Paper>
        )}

        {/* Statistics Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {statCards.map((card, index) => (
            <Grid item xs={12} sm={6} md={4} lg={2} key={index}>
              <Card 
                sx={{ 
                  height: "100%",
                  borderRadius: theme.shape.borderRadius,
                  boxShadow: theme.shadows[1],
                  transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
                  "&:hover": {
                    transform: "translateY(-3px)",
                    boxShadow: theme.shadows[4]
                  }
                }}
              >
                <CardContent>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    spacing={1}
                  >
                    <Box>
                      <Typography 
                        variant="overline" 
                        sx={{ 
                          fontWeight: 600,
                          fontSize: "0.7rem",
                          color: "text.secondary" 
                        }}
                      >
                        {card.title}
                      </Typography>
                      <Typography 
                        variant="h4" 
                        sx={{ 
                          fontWeight: "bold",
                          color: "text.primary" 
                        }}
                      >
                        {card.value}
                      </Typography>
                    </Box>
                    <Avatar 
                      sx={{ 
                        bgcolor: card.color,
                        width: 48,
                        height: 48
                      }}
                    >
                      <SvgIcon>{card.icon}</SvgIcon>
                    </Avatar>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Tabs Navigation */}
        <Paper sx={{ 
          mb: 3, 
          borderRadius: theme.shape.borderRadius,
          boxShadow: theme.shadows[1]
        }}>
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange}
            variant="fullWidth"
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label={i18n.t("dashboard.tabs.performance")} />
            <Tab label={i18n.t("dashboard.tabs.assessments")} />
            <Tab label={i18n.t("dashboard.tabs.attendants")} />
          </Tabs>
        </Paper>

        {/* Tab Panels */}
        {/* Performance Tab */}
        {activeTab === 0 && (
          <Paper 
            sx={{ 
              p: 3, 
              borderRadius: theme.shape.borderRadius,
              boxShadow: theme.shadows[1]
            }}
          >
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              {i18n.t("dashboard.charts.performance")}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <ChartsDate />
          </Paper>
        )}

        {/* Assessments Tab - NPS Data */}
        {activeTab === 1 && (
          <Paper 
            sx={{ 
              p: 3, 
              borderRadius: theme.shape.borderRadius,
              boxShadow: theme.shadows[1]
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Typography variant="h6" fontWeight="bold">
                {i18n.t("dashboard.tabs.assessments")}
              </Typography>
            </Box>
            <Divider sx={{ mb: 3 }} />
            
            <Grid container spacing={3}>
              {/* Main NPS Score */}
              <Grid item xs={12} md={3}>
                <Card 
                  sx={{ 
                    height: "100%",
                    borderRadius: theme.shape.borderRadius,
                    boxShadow: theme.shadows[1],
                    bgcolor: "#f8f9fa" 
                  }}
                >
                  <CardContent>
                    <ChartDonut
                      data={[
                        `{'name': 'Promotores', 'value': ${counters.npsPromotersPerc || 100}}`,
                        `{'name': 'Detratores', 'value': ${counters.npsDetractorsPerc || 0}}`,
                        `{'name': 'Neutros', 'value': ${counters.npsPassivePerc || 0}}`
                      ]}
                      value={counters.npsScore || 0}
                      title="Score"
                      color={(parseInt(counters.npsPromotersPerc || 0) + parseInt(counters.npsDetractorsPerc || 0) + parseInt(counters.npsPassivePerc || 0)) === 0 ? ["#918F94"] : [theme.palette.success.main, theme.palette.error.main, theme.palette.warning.main]}
                    />
                  </CardContent>
                </Card>
              </Grid>

              {/* Promoters */}
              <Grid item xs={12} md={3}>
                <Card 
                  sx={{ 
                    height: "100%",
                    borderRadius: theme.shape.borderRadius,
                    boxShadow: theme.shadows[1],
                    bgcolor: "#f8f9fa" 
                  }}
                >
                  <CardContent>
                    <ChartDonut
                      title={i18n.t("dashboard.assessments.prosecutors")}
                      value={counters.npsPromotersPerc || 0}
                      data={[`{'name': 'Promotores', 'value': 100}`]}
                      color={[theme.palette.success.main]}
                    />
                  </CardContent>
                </Card>
              </Grid>

              {/* Neutral */}
              <Grid item xs={12} md={3}>
                <Card 
                  sx={{ 
                    height: "100%",
                    borderRadius: theme.shape.borderRadius,
                    boxShadow: theme.shadows[1],
                    bgcolor: "#f8f9fa" 
                  }}
                >
                  <CardContent>
                    <ChartDonut
                      data={[`{'name': 'Neutros', 'value': 100}`]}
                      title={i18n.t("dashboard.assessments.neutral")}
                      value={counters.npsPassivePerc || 0}
                      color={[theme.palette.warning.main]}
                    />
                  </CardContent>
                </Card>
              </Grid>

              {/* Detractors */}
              <Grid item xs={12} md={3}>
                <Card 
                  sx={{ 
                    height: "100%",
                    borderRadius: theme.shape.borderRadius,
                    boxShadow: theme.shadows[1],
                    bgcolor: "#f8f9fa" 
                  }}
                >
                  <CardContent>
                    <ChartDonut
                      data={[`{'name': 'Detratores', 'value': 100}`]}
                      title={i18n.t("dashboard.assessments.detractors")}
                      value={counters.npsDetractorsPerc || 0}
                      color={[theme.palette.error.main]}
                    />
                  </CardContent>
                </Card>
              </Grid>

              {/* Assessment Summary */}
              <Grid item xs={12}>
                <Card 
                  sx={{ 
                    borderRadius: theme.shape.borderRadius,
                    boxShadow: theme.shadows[1],
                    bgcolor: "#f8f9fa" 
                  }}
                >
                  <CardContent>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <Box sx={{ textAlign: "center", p: 2 }}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            {i18n.t("dashboard.assessments.totalCalls")}
                          </Typography>
                          <Typography variant="h4" fontWeight="bold" color="primary">
                            {counters.tickets || 0}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Box sx={{ textAlign: "center", p: 2 }}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            {i18n.t("dashboard.assessments.ratedCalls")}
                          </Typography>
                          <Typography variant="h4" fontWeight="bold" color="primary">
                            {counters.withRating || 0}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Box sx={{ textAlign: "center", p: 2 }}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            {i18n.t("dashboard.assessments.evaluationIndex")}
                          </Typography>
                          <Typography variant="h4" fontWeight="bold" color="primary">
                            {Number(counters.percRating / 100 || 0).toLocaleString(undefined, { style: 'percent' })}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Paper>
        )}

        {/* Attendants Tab */}
        {activeTab === 2 && (
          <Paper 
            sx={{ 
              p: 3, 
              borderRadius: theme.shape.borderRadius,
              boxShadow: theme.shadows[1]
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Typography variant="h6" fontWeight="bold">
                {i18n.t("dashboard.tabs.attendants")}
              </Typography>
              
              <IconButton 
                onClick={exportarGridParaExcel} 
                color="primary"
                size="small"
                sx={{ 
                  bgcolor: "rgba(53, 152, 220, 0.1)",
                  "&:hover": {
                    bgcolor: "rgba(53, 152, 220, 0.2)"
                  }
                }}
              >
                <SaveAlt />
              </IconButton>
            </Box>
            <Divider sx={{ mb: 3 }} />

            <div id="grid-attendants">
              {attendants.length > 0 && (
                <TableAttendantsStatus 
                  attendants={attendants} 
                  loading={loading} 
                />
              )}
            </div>

            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                {i18n.t("dashboard.charts.userPerformance")}
              </Typography>
              <ChatsUser />
            </Box>
          </Paper>
        )}
      </Container>
    </Box>
  );
};

export default Dashboard;